/**
 * Derived threshold definitions for the Structural Health Measurement (SHM)
 * thickloss (`thickLoss`, µm) chart. Framework-agnostic so both the frontend
 * chart and any backend alerting can share the same formulas.
 */

/**
 * Fixed corrosion-rate level (icorr, µA/cm²) at which the orange ("moderate
 * risk") thickloss band's crossover term is read off the thickloss series.
 * Confirmed fixed by research — kept as a named constant, not configurable.
 */
export const ICORR_CROSSOVER_THRESHOLD = 0.2;

export interface ThickLossStructuralLimitParams {
  /** Rebar cover C (mm). */
  cover?: number;
  /** Rebar diameter (mm) — `channel.parameters.diameter`. */
  diameter?: number;
  /** Concrete design compressive strength fck (MPa). */
  fck?: number;
}

/**
 * Coerce a param to a finite number, or `null`. Channel `parameters` is a Mongo
 * `Mixed` field and the measurement form's numeric inputs can serialise as
 * strings ("25"), so values reach here as `number | string | null | undefined`.
 * Empty string / whitespace / non-numeric text → `null` (not `0`, unlike raw
 * `Number("")`).
 */
const toFiniteNumber = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    if (v.trim() === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

/**
 * RILEM / Andrade–Alonso structural-limit thickloss (µm) — the "high risk"
 * (red) band's lower bound:
 *
 *   83.8 + 7.4·(C / diameter) − 7.53·∛(fck²)
 *
 * Params may arrive as numbers or numeric strings (see {@link toFiniteNumber}).
 * Returns `null` unless `cover`, `diameter` (non-zero) and `fck` all coerce to
 * finite numbers.
 */
export function thickLossStructuralLimit(
  p: ThickLossStructuralLimitParams,
): number | null {
  const cover = toFiniteNumber(p.cover);
  const diameter = toFiniteNumber(p.diameter);
  const fck = toFiniteNumber(p.fck);
  if (cover === null || diameter === null || fck === null) return null;
  if (diameter === 0) return null;

  return 83.8 + 7.4 * (cover / diameter) - 7.53 * Math.cbrt(fck * fck);
}

/**
 * ThickLoss (µm) at the first sample where `icorr` first reaches `threshold`
 * (default {@link ICORR_CROSSOVER_THRESHOLD}) — the crossover term "A" that
 * feeds the orange ("moderate risk") band's lower bound.
 *
 * Non-finite `icorr` samples are skipped. Returns `null` when the threshold is
 * never reached, the series are empty or mismatched in length, or the matched
 * `thickLoss` sample is non-finite.
 */
export function thickLossAtIcorrCrossover(
  icorr: readonly number[],
  thickLoss: readonly number[],
  threshold: number = ICORR_CROSSOVER_THRESHOLD,
): number | null {
  if (!icorr?.length || icorr.length !== thickLoss?.length) return null;

  for (let i = 0; i < icorr.length; i++) {
    const c = icorr[i];
    if (!Number.isFinite(c)) continue;
    if (c >= threshold) {
      const tl = thickLoss[i];
      return Number.isFinite(tl) ? tl : null;
    }
  }
  return null;
}
